import React, { useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { GameCategory } from "@nestcoinco/hyper-api-gateway-api-client";

import ChevronDownOutlineIcon from "../../../components/icons/ChevronDownOutlineIcon";
import { cx } from "../../../utils/cx";
import ImageActivePlayers from "../../../images/active-players-green.svg";
import ImageCashStack from "../../../images/cash-stack.svg";
import ImageCrossedSwords from "../../../images/crossed-swords.svg";
import ImageFallbackGenreIcon from "../../../images/game-controller.svg";
import ImageGamepad from "../../../images/gamepad.svg";
import ImageNumberOne from "../../../images/number-1.svg";
import ImageNumberTwo from "../../../images/number-2.svg";
import ImageNumberThree from "../../../images/number-3.svg";
import ImagePeoplePair from "../../../images/people-pair.svg";
import ImageResponsibleGaming from "../../../images/responsible-gaming.svg";
import ImageShieldCheck from "../../../images/shield-check.svg";
import ImageTotalWinnings from "../../../images/total-winnings.svg";
import ImageWelcomeBonusGift from "../../../images/claim-bonus-box.svg";

interface GenrePillsProps {
  gameCategories?: GameCategory[];
}

export function GenrePills({ gameCategories }: GenrePillsProps) {
  const categories = useMemo(() => {
    const seenNames = new Set<string>();

    return (gameCategories ?? [])
      .filter((category) => {
        const normalizedName = category.name?.trim().toLowerCase();
        if (!normalizedName || seenNames.has(normalizedName)) return false;

        seenNames.add(normalizedName);
        return true;
      })
      .slice(0, 6);
  }, [gameCategories]);

  if (!categories.length) return null;

  return (
    <div className="genre-rail">
      <div className="genre-track">
        {Array.from({ length: 4 }).map((_, groupIndex) => (
          <div
            key={groupIndex}
            className="genre-list"
            aria-hidden={groupIndex > 0}
          >
            {categories.map((category) => (
              <div
                key={`${category.id}-${groupIndex}`}
                className="genre-pill"
              >
                <img
                  src={category.icon || ImageFallbackGenreIcon}
                  alt=""
                  className="genre-icon"
                  onError={(event) => {
                    event.currentTarget.onerror = null;
                    event.currentTarget.src = ImageFallbackGenreIcon;
                  }}
                />
                <span className="genre-name">
                  {category.name?.trim()}
                </span>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function StatsSection() {
  const { t } = useTranslation("games", {
    keyPrefix: "game_landing",
  });

  return (
    <section className="stats">
      <div className="stat">
        <img src={ImageActivePlayers} alt="" />
        <strong>150K+</strong>
        <span>{t("active_players")}</span>
      </div>
      <div className="stats-divider" />
      <div className="stat">
        <img src={ImageTotalWinnings} alt="" />
        <strong>₦500M</strong>
        <span>{t("total_winnings_paid")}</span>
      </div>
    </section>
  );
}

export function HowItWorksSection() {
  const { t } = useTranslation("games", {
    keyPrefix: "game_landing.how_it_works",
  });
  const steps = [
    {
      icon: ImageGamepad,
      number: ImageNumberOne,
      title: t("select_game"),
      description: t("select_game_description"),
    },
    {
      icon: ImageCrossedSwords,
      number: ImageNumberTwo,
      title: t("compete"),
      description: t("compete_description"),
    },
    {
      icon: ImageCashStack,
      number: ImageNumberThree,
      title: t("win_cash"),
      description: t("win_cash_description"),
    },
  ];

  return (
    <section className="how-it-works">
      <h2>{t("title")}</h2>
      {steps.map((step, index) => (
        <React.Fragment key={step.title}>
          {index > 0 ? (
            <div className="how-it-works-divider" />
          ) : null}
          <article className="how-it-works-step">
            <img
              src={step.icon}
              alt=""
              className="how-it-works-icon"
            />
            <div className="how-it-works-copy">
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </div>
            <img
              src={step.number}
              alt=""
              className="how-it-works-number"
            />
          </article>
        </React.Fragment>
      ))}
    </section>
  );
}

interface WelcomeBonusSectionProps {
  bonusPercent?: number;
  onClaim: () => void;
  disabled?: boolean;
}

export function WelcomeBonusSection({
  bonusPercent = 100,
  onClaim,
  disabled,
}: WelcomeBonusSectionProps) {
  const { t } = useTranslation("games", {
    keyPrefix: "game_landing.welcome_bonus",
  });
  const { t: tDashboard } = useTranslation("dashboard");

  return (
    <section className="bonus-section">
      <div className="bonus">
        <img
          src={ImageWelcomeBonusGift}
          alt=""
          className="bonus-image"
        />
        <div className="bonus-content">
          <div className="bonus-text">
            <h3 className="bonus-title">
              {t("title_prefix")}{" "}
              <span className="bonus-percent">
                {bonusPercent}%
              </span>{" "}
              {t("title_suffix")}
            </h3>
            <p className="bonus-description">{t("description")}</p>
          </div>
          <button
            type="button"
            className="bonus-button"
            onClick={onClaim}
            disabled={disabled}
          >
            {tDashboard("claim_bonus")}
          </button>
        </div>
      </div>
    </section>
  );
}

interface GameLandingPageFaq {
  question: string;
  answer: string;
}

function useGameLandingPageFaqs(): GameLandingPageFaq[] {
  const { t } = useTranslation("games", {
    keyPrefix: "game_landing.faq",
  });

  return [
    {
      question: t("what_is_hyper"),
      answer: t("what_is_hyper_answer"),
    },
    {
      question: t("what_kind_of_games"),
      answer: t("what_kind_of_games_answer"),
    },
    {
      question: t("will_i_need_to_deposit"),
      answer: t("will_i_need_to_deposit_answer"),
    },
    {
      question: t("receive_winnings"),
      answer: t("receive_winnings_answer"),
    },
  ];
}

interface FaqItemProps {
  faq: GameLandingPageFaq;
  isOpen: boolean;
  onToggle: () => void;
}

function FaqItem({ faq, isOpen, onToggle }: FaqItemProps) {
  const questionId = useId();
  const answerId = useId();

  return (
    <article className={cx("faq-item", isOpen && "is-open")}>
      <button
        type="button"
        className="faq-question"
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={answerId}
      >
        <span id={questionId}>{faq.question}</span>
        <ChevronDownOutlineIcon
          className="faq-icon"
          aria-hidden="true"
        />
      </button>
      <p
        id={answerId}
        className="faq-answer"
        role="region"
        aria-labelledby={questionId}
        hidden={!isOpen}
      >
        {faq.answer}
      </p>
    </article>
  );
}

export function FaqSection() {
  const { t } = useTranslation("games", {
    keyPrefix: "game_landing.faq",
  });
  const faqs = useGameLandingPageFaqs();
  const [openIndexes, setOpenIndexes] = useState<number[]>([0]);

  const toggleFaq = (index: number) => {
    setOpenIndexes((currentIndexes) =>
      currentIndexes.includes(index)
        ? currentIndexes.filter((value) => value !== index)
        : [...currentIndexes, index]
    );
  };

  return (
    <section className="faq">
      <h2 className="faq-title">{t("title")}</h2>
      <div className="faq-list">
        {faqs.map((faq, index) => (
          <FaqItem
            key={faq.question}
            faq={faq}
            isOpen={openIndexes.includes(index)}
            onToggle={() => toggleFaq(index)}
          />
        ))}
      </div>
    </section>
  );
}

export function SafetySection() {
  const { t } = useTranslation("games", {
    keyPrefix: "game_landing.safety",
  });
  const safetyItems = [
    {
      icon: ImageResponsibleGaming,
      title: t("age_only"),
      description: t("play_responsibly"),
    },
    {
      icon: ImagePeoplePair,
      title: t("fair_play"),
      description: t("fair_play_description"),
    },
    {
      icon: ImageShieldCheck,
      title: t("secure_verified"),
      description: t("secure_verified_description"),
    },
  ];

  return (
    <section className="safety">
      {safetyItems.map((item, index) => (
        <React.Fragment key={item.title}>
          {index > 0 ? (
            <div className="safety-divider" />
          ) : null}
          <article className="safety-item">
            <img
              src={item.icon}
              alt=""
              className="safety-icon"
            />
            <div className="safety-copy">
              <h3>{item.title}</h3>
              <p>{item.description}</p>
            </div>
          </article>
        </React.Fragment>
      ))}
    </section>
  );
}
